"use client";
import { FirebaseApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  getFirestore,
  onSnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import PATHS from '../utils/paths';
import { confirmStripeAttribution, usingReferralBear } from './affiliate_attribution.ts';

export const STARTER_YEARLY_PRICE_ID = "price_1QxDUnEZfFKA3aBZlhWkdVbP";
export const STARTER_MONTHLY_PRICE_ID = "price_1QQdu2EZfFKA3aBZLed75UnU";
export const BUSINESS_YEARLY_PRICE_ID = "price_1QQdtOEZfFKA3aBZl3La27u8";
export const BUSINESS_MONTHLY_PRICE_ID = "price_1QQds9EZfFKA3aBZcGeNHOJB";

export const getCheckoutUrl = async (
  app: FirebaseApp,
  priceId: string,
): Promise<string> => {
  const auth = getAuth(app);
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("User is not authenticated");
  const user = auth.currentUser!;

  const toltReferral = usingReferralBear ? null : (window as any).tolt_referral || null;
  let referralBearClickId: string | null = null;
  if (usingReferralBear) {
    try {
      referralBearClickId = await confirmStripeAttribution(user);
    } catch (error) {
      console.error("Affiliate attribution could not be confirmed; checkout continues", error);
    }
  }

  const db = getFirestore(app);
  const checkoutSessionRef = collection(
    db,
    "customers",
    userId,
    "checkout_sessions"
  );

  // First attempt with promotion code if there's a referral
  if (toltReferral) {
    try {
      const result = await tryCreateCheckoutSession(checkoutSessionRef, priceId, toltReferral, referralBearClickId);
      return result;
    } catch (error) {
      if (error instanceof Error && error.message.includes('prior transactions')) {
        // Retry without promotion code
        return tryCreateCheckoutSession(checkoutSessionRef, priceId, toltReferral, referralBearClickId);
      }
      throw error;
    }
  }

  // No referral, proceed normally
  return tryCreateCheckoutSession(checkoutSessionRef, priceId, toltReferral, referralBearClickId);
};

const tryCreateCheckoutSession = async (
  checkoutSessionRef: any,
  priceId: string,
  toltReferral: string | null,
  referralBearClickId: string | null,
): Promise<string> => {
  const docRef = await addDoc(checkoutSessionRef, {
    price: priceId,
    metadata: referralBearClickId
      ? { referral_bear_click_id: referralBearClickId }
      : toltReferral ? { tolt_referral: toltReferral } : {},
    success_url: `${window.location.origin}${PATHS.PREMIUM_UNLOCKED}`,
    cancel_url: `${window.location.origin}${PATHS.PRICING}`,
    allow_promotion_codes: true,
    trial_period_days: 7,
  });

  return new Promise<string>((resolve, reject) => {
    const unsubscribe = onSnapshot(docRef, (snap) => {
      const { error, url } = snap.data() as {
        error?: { message: string };
        url?: string;
      };
      if (error) {
        unsubscribe();
        reject(new Error(error.message));
      }
      if (url) {
        console.log("Stripe Checkout URL:", url);
        unsubscribe();
        resolve(url);
      }
    });
  });
};

export const getPortalUrl = async (app: FirebaseApp): Promise<string> => {
  const auth = getAuth(app);
  const user = auth.currentUser;

  let dataWithUrl: any;
  try {
    const functions = getFunctions(app, "us-central1");
    const functionRef = httpsCallable(
      functions,
      "ext-firestore-stripe-payments-createPortalLink"
    );
    const { data } = await functionRef({
      customerId: user?.uid,
      returnUrl: window.location.origin,
    });

    // Add a type to the data
    dataWithUrl = data as { url: string };
    console.log("Reroute to Stripe portal: ", dataWithUrl.url);
  } catch (error) {
    console.error(error);
  }

  return new Promise<string>((resolve, reject) => {
    if (dataWithUrl.url) {
      resolve(dataWithUrl.url);
    } else {
      reject(new Error("No url returned"));
    }
  });
};
